import { AuthenticationService } from '@aize/authentication';
import { inject, Injectable, signal } from '@angular/core';
import { NotificationsStateService } from '@app/core/notifications/notifications-state.service';
import { environment } from '@environment/environment';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { firstValueFrom } from 'rxjs';
import { Notification } from '../models/notification';

@Injectable({ providedIn: 'root' })
export class NotificationsSignalrService {
  private readonly authService = inject(AuthenticationService);
  private readonly notificationStateService = inject(NotificationsStateService);

  private hub: HubConnection | null = null;
  readonly connected = signal(false);

  ngOnDestroy() {
    this.stopConnection();
  }

  public async initialize(): Promise<void> {
    this.notificationStateService.initialise();
    await this.startConnection();
  }

  private async startConnection(): Promise<void> {
    if (this.hub && this.hub.state !== HubConnectionState.Disconnected) return;

    this.hub = new HubConnectionBuilder()
      .withUrl(`${environment.notificationsApi}hubs/notifications`, {
        accessTokenFactory: async () => this.getAccessToken()
      })
      .withAutomaticReconnect()
      .build();

    this.hub.on('ReceiveNotification', (incoming: Notification) => {
      this.notificationStateService.incomingNotification(incoming);
    });

    try {
      await this.hub.start();
      this.connected.set(true);
    } catch (error) {
      console.error('Failed to start notifications SignalR connection', error);
      this.connected.set(false);
    }
  }

  private async stopConnection(): Promise<void> {
    if (!this.hub) return;

    try {
      await this.hub.stop();
    } finally {
      this.hub = null;
      this.connected.set(false);
    }
  }

  // SignalR expects the token in the query string for WebSocket connections. I have no clue how to make it work with the Authorization header, so I have to get the token manually open for suggestions on how to improve this.
  private async getAccessToken(): Promise<string> {
    try {
      return await firstValueFrom(this.authService.accessToken$);
    } catch {
      return '';
    }
  }
}
